import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Search, Plus, Users, Loader2, Globe, Shield, PlusCircle } from "lucide-react";
import { getGroups, getGroupCategories, joinGroup, leaveGroup } from "@/lib/api";
import { GroupCard, CreateGroupModal } from "@/components/group";

export default function GroupsPage({ currentUser }) {
  const [groups, setGroups] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [activeTab, setActiveTab] = useState("my-groups");
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [joinLoading, setJoinLoading] = useState(null);

  useEffect(() => {
    loadCategories();
    loadGroups();
  }, [activeTab, selectedCategory]);

  const loadCategories = async () => {
    try {
      const res = await getGroupCategories();
      setCategories(res.data.categories || []);
    } catch {
      setCategories([]);
    }
  };

  const loadGroups = async () => {
    setLoading(true);
    try {
      const params = {};
      if (activeTab === "my-groups") params.my_groups = true;
      if (selectedCategory) params.category = selectedCategory;
      if (searchQuery) params.search = searchQuery;
      
      const res = await getGroups(params);
      setGroups(res.data.groups || []);
    } catch {
      toast.error("Failed to load groups");
    }
    setLoading(false);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    loadGroups();
  };

  const handleJoin = async (groupId) => {
    setJoinLoading(groupId);
    try {
      const res = await joinGroup(groupId);
      if (res.data.status === "joined") {
        toast.success("Joined group successfully!");
      } else {
        toast.success("Join request sent!");
      }
      loadGroups();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to join group");
    }
    setJoinLoading(null);
  };

  const handleLeave = async (groupId) => {
    setJoinLoading(groupId);
    try {
      await leaveGroup(groupId);
      toast.success("Left group");
      loadGroups();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to leave group");
    }
    setJoinLoading(null);
  };

  return (
    <div className="min-h-screen bg-[#F9F7F2] p-4 md:p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="font-['Crimson_Text'] text-2xl sm:text-3xl font-bold text-[#2D2A26]">Groups</h1>
            <p className="text-sm text-[#6B665E] font-['Nunito'] mt-1">
              Connect with parishioners who share your interests
            </p>
          </div>
          <Button 
            onClick={() => setCreateModalOpen(true)}
            className="bg-[var(--liturgical-primary)] hover:brightness-90 text-white font-['Nunito']"
            data-testid="create-group-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Group
          </Button>
        </div>

        {/* Search and Filters */}
        <Card className="bg-white border-[#E5E0D5] mb-6">
          <CardContent className="p-4">
            <form onSubmit={handleSearch} className="flex flex-col md:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#6B665E]" />
                <Input 
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search groups..."
                  className="pl-10 border-[#E5E0D5] font-['Nunito']"
                  data-testid="groups-search-input"
                />
              </div>
              
              <Select value={selectedCategory || "all"} onValueChange={(v) => setSelectedCategory(v === "all" ? "" : v)}>
                <SelectTrigger className="w-full md:w-48 border-[#E5E0D5] font-['Nunito']">
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent className="bg-white max-h-60">
                  <SelectItem value="all" className="font-['Nunito']">All Categories</SelectItem>
                  {categories.map(cat => (
                    <SelectItem key={cat} value={cat} className="font-['Nunito']">{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Button type="submit" className="bg-[var(--liturgical-primary)] hover:brightness-90 text-white font-['Nunito']">
                Search
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
          <TabsList className="bg-white border border-[#E5E0D5]">
            <TabsTrigger 
              value="my-groups" 
              className="font-['Nunito'] data-[state=active]:bg-[var(--liturgical-primary)] data-[state=active]:text-white"
              data-testid="my-groups-tab"
            >
              <Users className="w-4 h-4 mr-2" />
              My Groups
            </TabsTrigger>
            <TabsTrigger 
              value="discover" 
              className="font-['Nunito'] data-[state=active]:bg-[var(--liturgical-primary)] data-[state=active]:text-white"
              data-testid="discover-groups-tab"
              title="Discover Groups"
            >
              <PlusCircle className="w-4 h-4" />
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Groups List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-[var(--liturgical-primary)]" />
          </div>
        ) : groups.length === 0 ? (
          <Card className="bg-white border-[#E5E0D5]">
            <CardContent className="py-12 text-center">
              <Users className="w-12 h-12 text-[#E5E0D5] mx-auto mb-4" />
              <h3 className="font-['Crimson_Text'] text-lg font-semibold text-[#2D2A26] mb-2">
                {activeTab === "my-groups" ? "You haven't joined any groups yet" : "No groups found"}
              </h3>
              <p className="text-sm text-[#6B665E] font-['Nunito'] mb-4">
                {activeTab === "my-groups" 
                  ? "Discover groups that match your interests" 
                  : "Try adjusting your search or create a new group"}
              </p>
              {activeTab === "my-groups" && (
                <Button 
                  onClick={() => setActiveTab("discover")}
                  className="bg-[var(--liturgical-primary)] hover:brightness-90 text-white font-['Nunito']"
                >
                  <Globe className="w-4 h-4 mr-2" />
                  Discover Groups
                </Button>
              )}
            </CardContent>
          </Card>
        ) : activeTab === "my-groups" ? (
          <MyGroupsSections groups={groups} currentUser={currentUser} onJoin={handleJoin} onLeave={handleLeave} joinLoading={joinLoading} />
        ) : (
          <div className="space-y-4">
            {groups.map(group => (
              <GroupCard 
                key={group.group_id} 
                group={group}
                onJoin={handleJoin}
                onLeave={handleLeave}
                joinLoading={joinLoading}
              />
            ))}
          </div>
        )}

        {/* Create Group Modal */}
        <CreateGroupModal 
          isOpen={createModalOpen}
          onClose={() => setCreateModalOpen(false)}
          categories={categories}
          onSuccess={loadGroups}
          currentUser={currentUser}
        />
      </div>
    </div>
  );
}

function MyGroupsSections({ groups, currentUser, onJoin, onLeave, joinLoading }) {
  const adminGroups = groups.filter(g => g.is_admin);
  const joinedGroups = groups.filter(g => !g.is_admin);
  
  return (
    <div className="space-y-6">
      {adminGroups.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Shield className="w-4 h-4 text-[var(--liturgical-primary)]" />
            <h2 className="font-['Crimson_Text'] text-lg font-semibold text-[#2D2A26]" data-testid="admin-groups-heading">
              Groups I Admin ({adminGroups.length})
            </h2>
          </div>
          <div className="space-y-3">
            {adminGroups.map(group => (
              <GroupCard key={group.group_id} group={group} onJoin={onJoin} onLeave={onLeave} joinLoading={joinLoading} />
            ))}
          </div>
        </div>
      )}
      
      {joinedGroups.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Users className="w-4 h-4 text-[#556B2F]" />
            <h2 className="font-['Crimson_Text'] text-lg font-semibold text-[#2D2A26]" data-testid="joined-groups-heading">
              Groups I've Joined ({joinedGroups.length})
            </h2>
          </div>
          <div className="space-y-3">
            {joinedGroups.map(group => (
              <GroupCard key={group.group_id} group={group} onJoin={onJoin} onLeave={onLeave} joinLoading={joinLoading} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}